import { useEffect, useState, useCallback, useRef } from "react";
import Navbar from "@/components/Navbar";
import { getDiscoverProfiles, swipeProfile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  MapPin, Heart, Users, Shield, Navigation, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown,
  Lock, Coins, Circle, Info, User, Briefcase, GraduationCap, Baby, Sparkles
} from "lucide-react";
import ProfileModal from "@/components/ProfileModal";
import FacebookGroupDialog, { useFacebookGroupPrompt } from "@/components/FacebookGroupDialog";
import goldBadge from "../assets/gold.png";
import platinumBadge from "../assets/platinum.png";
import blueTick from "../assets/blue.png";
import ReportButton from "@/components/ReportButton";

function timeAgo(date) {
  if (!date) return "";
  const now = new Date();
  const active = new Date(date);
  const diffMs = now - active;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return "Active just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Active ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Active ${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Active ${days}d ago`;
  if (days < 30) return `Active ${Math.floor(days / 7)}w ago`;
  if (days < 365) return `Active ${Math.floor(days / 30)}mo ago`;
  return `Active ${Math.floor(days / 365)}y ago`;
}

/* ---------- Skeleton card shown while the first page loads ---------- */
function ProfileCardSkeleton() {
  return (
    <div className="bg-white border border-[#E7E5E0] rounded-2xl overflow-hidden shadow-sm max-w-4xl mx-auto md:h-[39rem] animate-pulse">
      <div className="flex flex-col md:flex-row h-full">
        <div className="w-full md:w-1/2 h-72 md:h-full bg-[#EFEDE7]" />
        <div className="flex-1 md:w-1/2 p-6 space-y-4">
          <div className="h-6 w-2/3 bg-[#EFEDE7] rounded-md" />
          <div className="h-3 w-1/3 bg-[#EFEDE7] rounded-md" />
          <div className="grid grid-cols-2 gap-3 pt-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-3 w-full bg-[#EFEDE7] rounded-md" />
            ))}
          </div>
          <div className="space-y-2 pt-2">
            <div className="h-3 w-full bg-[#EFEDE7] rounded-md" />
            <div className="h-3 w-5/6 bg-[#EFEDE7] rounded-md" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Find() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [swipingProfiles, setSwipingProfiles] = useState({});
  const [galleryIndexes, setGalleryIndexes] = useState({});
  const [noTokens, setNoTokens] = useState(false);
  const [expandedBio, setExpandedBio] = useState({});
  const [panelCollapsed, setPanelCollapsed] = useState({});
  const observerRef = useRef(null);

  // Facebook group promotion prompt
  const fbPrompt = useFacebookGroupPrompt();

  // Whether we're on a mobile-sized viewport (aligns with Tailwind md: 768px)
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 767px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobileViewport(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const isViewerPremium = user?.premium_tier && user?.premium_expires_at && new Date(user.premium_expires_at) > new Date();

  useEffect(() => { loadProfiles(1); }, []);

  // Track every profile the user has seen for the Facebook group prompt.
  // markProfileSeen is idempotent — already-counted IDs are skipped — so
  // calling it for the full array on every render is safe.
  useEffect(() => {
    profiles.forEach((p) => fbPrompt.markProfileSeen(p.user_id));
  }, [profiles, fbPrompt.markProfileSeen]);

  const loadProfiles = async (pageNum) => {
    if (noTokens) return;
    if (!isViewerPremium && user?.tokens < 2) {
      setNoTokens(true);
      setLoading(false);
      setLoadingMore(false);
      toast.error("You need at least 2 tokens to load profiles. Earn tokens or upgrade to Premium.");
      return;
    }
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const data = await getDiscoverProfiles({ page: pageNum, limit: 2 });
      if (pageNum === 1) setProfiles(data);
      else setProfiles(prev => [...prev, ...data]);

      // Server may return fewer than `limit` rows when the block filter
      // removes them after pagination. A short page is still "keep
      // going" — only an EMPTY page means we've reached the end.
      setHasMore(data.length > 0);
      setNoTokens(false);
    } catch (err) {
      if (err.response?.status === 402) {
        setNoTokens(true);
        toast.error("You ran out of tokens. Earn them or upgrade to Premium.");
      } else {
        toast.error("Failed to load profiles");
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const lastProfileRef = useCallback((node) => {
    if (loadingMore || noTokens) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        loadProfiles(nextPage);
      }
    }, { threshold: 0.3 });
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, page, noTokens]);

  const getAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birth = new Date(dateOfBirth);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleSwipe = async (profile, type) => {
    if (swipingProfiles[profile.user_id]) return;
    setSwipingProfiles(prev => ({ ...prev, [profile.user_id]: true }));
    try {
      const swipeType = type === "friend" ? "friendship" : "dating";
      const result = await swipeProfile(profile.user_id, "like", swipeType);
      if (result.matched) toast.success("It's a match!", { description: `You matched with ${profile.display_name || "someone"}.` });
      else toast.success(type === "dating" ? "Like sent" : "Friend request sent");
    } catch (err) { toast.error("Action failed"); }
    finally { setTimeout(() => setSwipingProfiles(prev => ({ ...prev, [profile.user_id]: false })), 2000); }
  };

  const getGalleryImages = (profile) => [profile.profile_image, ...(profile.gallery_images || [])].filter(Boolean);
  const isImageLocked = (index, profile) => {
    if (isViewerPremium) return false;
    if (profile.lock_all_images) return true;
    return index >= 2;
  };

  const handlePrevImage = (e, profile) => {
    e.stopPropagation();
    const images = getGalleryImages(profile);
    setGalleryIndexes(prev => ({
      ...prev,
      [profile.user_id]: (prev[profile.user_id] || 0) === 0 ? images.length - 1 : (prev[profile.user_id] || 0) - 1
    }));
  };
  const handleNextImage = (e, profile) => {
    e.stopPropagation();
    const images = getGalleryImages(profile);
    setGalleryIndexes(prev => ({
      ...prev,
      [profile.user_id]: (prev[profile.user_id] || 0) === images.length - 1 ? 0 : (prev[profile.user_id] || 0) + 1
    }));
  };

  const getBadges = (profile) => {
    const badges = [];
    if (profile.verified) badges.push({ key: "blue", src: blueTick, alt: "Verified" });
    if (profile.premium_tier === "gold") badges.push({ key: "gold", src: goldBadge, alt: "Gold member" });
    else if (profile.premium_tier === "platinum") badges.push({ key: "platinum", src: platinumBadge, alt: "Platinum member" });
    return badges;
  };

  const toggleBio = (userId) => {
    setExpandedBio(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const togglePanel = (userId) => {
    setPanelCollapsed((prev) => ({
      ...prev,
      // On mobile the default state is collapsed; on desktop it's expanded.
      [userId]: !(prev[userId] ?? isMobileViewport),
    }));
  };

  /* ---------- Out of tokens, no profiles loaded at all ---------- */
  if (noTokens && !loading && profiles.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh] px-4">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto mb-5">
              <Coins className="w-7 h-7 text-amber-500" />
            </div>
            <h2 className="font-display text-2xl font-semibold text-gray-900 mb-2">You're out of tokens</h2>
            <p className="text-[#6B6B70] text-sm mb-7 leading-relaxed">
              Each profile costs 1 token to view. Earn more for free, or go Premium for unlimited browsing.
            </p>
            <div className="flex gap-3 justify-center">
              <a
                href="/earn-tokens"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-5 py-2.5 text-sm font-medium text-amber-700 transition-colors hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400"
              >
                <Coins className="w-4 h-4" /> Earn tokens
              </a>
              <a
                href="/shop"
                className="inline-flex items-center gap-1.5 rounded-full bg-purple-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-purple-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400"
              >
                <Sparkles className="w-4 h-4" /> Go Premium
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <div className="max-w-4xl mx-auto mb-4 sm:mb-6">
          <h1 className="font-display text-2xl font-semibold text-gray-900">Discover</h1>
          <p className="text-sm text-[#6B6B70] mt-1">People near you, matched to what you're looking for.</p>
        </div>

        {loading ? (
          <div className="space-y-6">
            <ProfileCardSkeleton />
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-20 max-w-sm mx-auto">
            <div className="w-14 h-14 rounded-full bg-purple-50 border border-purple-100 flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-purple-500" />
            </div>
            <h2 className="font-display text-lg font-semibold text-gray-900 mb-1">No profiles right now</h2>
            <p className="text-sm text-[#6B6B70]">Check back soon, or widen your filters to see more people.</p>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {profiles.map((profile, index) => {
              const age = getAge(profile.date_of_birth);
              const isLast = index === profiles.length - 1;
              const isSwiping = swipingProfiles[profile.user_id];
              const galleryImages = getGalleryImages(profile);
              const currentImageIndex = galleryIndexes[profile.user_id] || 0;
              const badges = getBadges(profile);
              const locked = isImageLocked(currentImageIndex, profile);
              const isOnline = profile.last_active && (new Date() - new Date(profile.last_active) < 5 * 60 * 1000);
              const interests = (profile.interests || "").split(",").map(s => s.trim()).filter(Boolean);
              const bioExpanded = expandedBio[profile.user_id] || false;
              // On mobile, default collapsed so the image dominates. On desktop, default expanded.
              const isCollapsed = panelCollapsed[profile.user_id] ?? isMobileViewport;

              return (
                <div
                  key={profile.user_id}
                  ref={isLast ? lastProfileRef : null}
                  className={`group bg-white border border-[#E7E5E0] rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:border-[#D8D5CE] transition-all duration-300 cursor-pointer mx-auto md:h-[39rem] ${
                    isCollapsed ? 'md:max-w-2xl' : 'md:max-w-4xl'
                  }`}
                  onClick={() => setSelectedProfile(profile)}
                >
                  <div className="flex flex-col md:flex-row h-full relative">
                    {/* ---------- Image container ---------- */}
                    <div className={`relative w-full shrink-0 bg-[#EFEDE7] transition-all duration-300 ${
                      isCollapsed
                        ? "h-[75vh] md:h-full md:w-full"
                        : "h-[55vh] sm:h-80 md:h-full md:w-1/2"
                    }`}>
                      {galleryImages.length > 0 ? (
                        <>
                          <img
                            src={galleryImages[currentImageIndex] || profile.profile_image}
                            alt={profile.display_name}
                            className={`absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02] ${locked ? "blur-md brightness-[0.55] scale-105" : ""}`}
                          />
                          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/35 to-transparent" />

                          {locked && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/25">
                              <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center mb-2 border border-white/30">
                                <Lock className="w-5 h-5 text-white" />
                              </div>
                              <span className="text-white text-xs font-semibold bg-black/50 px-3 py-1 rounded-full tracking-wide">
                                PREMIUM ONLY
                              </span>
                            </div>
                          )}

                          <div className="absolute left-3 top-3 z-20">
                            <ReportButton type="photo" reportedUserId={profile.user_id} imageIndex={currentImageIndex} />
                          </div>

                          {profile.health_status && profile.health_status !== "none" && (
                            <div className="absolute top-3 right-3 px-2.5 py-1 bg-black/50 backdrop-blur-sm rounded-full text-xs text-white font-medium flex items-center gap-1 z-20">
                              <Shield className="w-3 h-3" />
                              {profile.health_status}
                            </div>
                          )}

                          {galleryImages.length > 1 && (
                            <>
                              <button
                                onClick={(e) => handlePrevImage(e, profile)}
                                aria-label="Previous photo"
                                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/35 rounded-full flex items-center justify-center text-white hover:bg-black/55 transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                              >
                                <ChevronLeft className="w-5 h-5" />
                              </button>
                              <button
                                onClick={(e) => handleNextImage(e, profile)}
                                aria-label="Next photo"
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/35 rounded-full flex items-center justify-center text-white hover:bg-black/55 transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                              >
                                <ChevronRight className="w-5 h-5" />
                              </button>
                            </>
                          )}

                          {galleryImages.length > 1 && (
                            <div className="absolute bottom-3 inset-x-0 z-20 flex items-center justify-center gap-1.5">
                              {galleryImages.map((_, i) => (
                                <span
                                  key={i}
                                  className={`h-1.5 rounded-full transition-all ${i === currentImageIndex ? "w-4 bg-white" : "w-1.5 bg-white/50"}`}
                                />
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <User className="w-16 h-16 text-gray-400" />
                        </div>
                      )}

                      {/* Mobile-only overlay — shows name / age / distance while collapsed,
                          so essential info is always visible in image-dominant view */}
                      {isCollapsed && (
                        <div className="md:hidden absolute inset-x-0 bottom-0 p-4 pt-20 bg-gradient-to-t from-black/85 to-transparent pointer-events-none">
                          <h3 className="text-white text-2xl font-semibold flex items-baseline gap-2">
                            {profile.display_name || "Anonymous"}
                            {age && <span className="text-xl font-normal text-white/85">{age}</span>}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-white/85 mt-1">
                            {profile.distance_km != null && (
                              <span>
                                {profile.distance_km <= 0.1 ? "Nearby" : `${profile.distance_km} km away`}
                              </span>
                            )}
                            {profile.country && (
                              <span>{profile.city ? `${profile.city}, ` : ""}{profile.country}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Mobile toggle bar — sits between image and info panel */}
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePanel(profile.user_id); }}
                      className="md:hidden flex items-center justify-center gap-1.5 min-h-[48px] px-4 bg-white border-y border-[#E7E5E0] text-sm font-semibold text-[#7C3AED] active:bg-gray-50"
                    >
                      {isCollapsed
                        ? <><ChevronUp className="w-4 h-4" /> Show profile info</>
                        : <><ChevronDown className="w-4 h-4" /> Hide profile info</>
                      }
                    </button>

                    {/* Desktop arrow toggle (absolutely positioned at the seam) */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePanel(profile.user_id);
                      }}
                      className="hidden md:flex absolute top-1/2 -translate-y-1/2 z-30 w-6 h-10 bg-white/80 backdrop-blur-sm border border-[#E7E5E0] rounded-r-lg items-center justify-center shadow-sm hover:bg-white transition-colors"
                      style={{ left: isCollapsed ? 'calc(100% - 1.5rem)' : '50%', transform: 'translate(-50%, -50%)' }}
                      aria-label={isCollapsed ? "Expand details" : "Collapse details"}
                    >
                      {isCollapsed ? <ChevronLeft className="w-4 h-4 text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-600" />}
                    </button>

                    {/* ---------- Info panel ---------- */}
                    <div className={`flex flex-col flex-1 md:w-1/2 p-4 sm:p-6 overflow-y-auto bg-white transition-all duration-300 ${
                      isCollapsed ? 'hidden' : ''
                    }`}>
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <h3 className="font-display text-xl sm:text-2xl font-semibold text-gray-900 flex items-baseline gap-2 leading-tight">
                          <span className="flex items-center gap-1.5">
                            {profile.display_name || "Anonymous"}
                            {badges.map(b => (
                              <img key={b.key} src={b.src} alt={b.alt} title={b.alt} className="w-5 h-5 inline-block" />
                            ))}
                          </span>
                          {age && <span className="text-lg text-gray-400 font-normal">{age}</span>}
                        </h3>
                        {profile.gender && (
                          <span className="shrink-0 px-2.5 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                            {profile.gender}
                          </span>
                        )}
                      </div>

                      {/* Online status */}
                      <div className="flex items-center gap-1.5 text-sm mb-3 sm:mb-4">
                        {isOnline ? (
                          <>
                            <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />
                            <span className="text-emerald-700 font-medium">Online now</span>
                          </>
                        ) : (
                          <span className="text-gray-400">{timeAgo(profile.last_active)}</span>
                        )}
                      </div>

                      {/* Key details */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mb-4 sm:mb-5 text-sm text-gray-600">
                        {profile.country && (
                          <div className="flex items-center gap-2 min-w-0">
                            <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="truncate">{profile.city ? `${profile.city}, ` : ''}{profile.country}</span>
                          </div>
                        )}
                        {profile.distance_km != null && (
                          <div className="flex items-center gap-2 min-w-0">
                            <Navigation className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="truncate">{profile.distance_km <= 0.1 ? "Nearby" : `${profile.distance_km} km away`}</span>
                          </div>
                        )}
                        {profile.health_status && profile.health_status !== "none" && (
                          <div className="flex items-center gap-2 min-w-0">
                            <Shield className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="truncate">{profile.health_status}</span>
                          </div>
                        )}
                        {profile.education && (
                          <div className="flex items-center gap-2 min-w-0">
                            <GraduationCap className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="truncate">{profile.education}</span>
                          </div>
                        )}
                        {profile.employment && (
                          <div className="flex items-center gap-2 min-w-0">
                            <Briefcase className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="truncate">{profile.employment}</span>
                          </div>
                        )}
                        {profile.kids && (
                          <div className="flex items-center gap-2 min-w-0">
                            <Baby className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="truncate">{profile.kids === 'have' ? 'Has kids' : 'No kids'}</span>
                          </div>
                        )}
                      </div>

                      {/* Bio */}
                      {profile.bio && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">About</h4>
                          <p className={`text-sm text-gray-700 leading-relaxed ${!bioExpanded ? 'line-clamp-3' : ''}`}>
                            {profile.bio}
                          </p>
                          {profile.bio.length > 150 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleBio(profile.user_id); }}
                              className="text-xs text-purple-600 hover:text-purple-800 font-medium mt-1 focus-visible:underline"
                            >
                              {bioExpanded ? 'Show less' : 'Read more'}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Interests */}
                      {interests.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Interests</h4>
                          <div className="flex flex-wrap gap-1.5">
                            {interests.slice(0, 6).map((int, i) => (
                              <span key={i} className="px-2.5 py-1 bg-gray-50 border border-gray-100 text-gray-600 rounded-full text-xs font-medium">
                                {int}
                              </span>
                            ))}
                            {interests.length > 6 && (
                              <span className="px-2.5 py-1 bg-gray-50 border border-gray-100 text-gray-400 rounded-full text-xs">
                                +{interests.length - 6} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="mt-auto flex flex-col items-center gap-3 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSwipe(profile, "dating"); }}
                            disabled={isSwiping}
                            aria-label="Like"
                            className="w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-600 text-white rounded-full shadow-sm hover:shadow-md hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-400"
                          >
                            <Heart className={`w-5 h-5 ${isSwiping ? "animate-pulse" : ""}`} fill="currentColor" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSwipe(profile, "friend"); }}
                            disabled={isSwiping}
                            aria-label="Add as friend"
                            className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 text-white rounded-full shadow-sm hover:shadow-md hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
                          >
                            <Users className={`w-5 h-5 ${isSwiping ? "animate-pulse" : ""}`} />
                          </button>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedProfile(profile); }}
                          className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors focus-visible:underline"
                        >
                          <Info className="w-3.5 h-3.5" /> View full profile
                        </button>
                      </div>

                      {/* Mobile-only "hide info" shortcut at the bottom of the info panel */}
                      <button
                        onClick={(e) => { e.stopPropagation(); togglePanel(profile.user_id); }}
                        className="md:hidden mt-4 flex items-center justify-center gap-1.5 min-h-[44px] w-full rounded-lg border border-[#E7E5E0] text-sm font-medium text-gray-600 hover:bg-gray-50 active:bg-gray-100"
                      >
                        <ChevronDown className="w-4 h-4" /> Back to photos
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {loadingMore && (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {noTokens && profiles.length > 0 && !loadingMore && (
              <div className="text-center py-6 px-4 bg-amber-50 rounded-2xl border border-amber-100 max-w-4xl mx-auto">
                <div className="w-10 h-10 rounded-full bg-white border border-amber-200 flex items-center justify-center mx-auto mb-3">
                  <Coins className="w-5 h-5 text-amber-500" />
                </div>
                <p className="text-sm font-medium text-gray-800 mb-3">You've used all your tokens</p>
                <div className="flex items-center justify-center gap-3">
                  <a href="/earn-tokens" className="inline-flex items-center gap-1.5 bg-white border border-amber-300 text-amber-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-amber-100 transition-colors">
                    Earn tokens
                  </a>
                  <a href="/shop" className="inline-flex items-center gap-1.5 bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-purple-700 transition-colors">
                    Go Premium
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      {selectedProfile && <ProfileModal profile={selectedProfile} onClose={() => setSelectedProfile(null)} isViewerPremium={isViewerPremium} />}

      {/* Facebook group promotion — fires after 10 unique profiles viewed */}
      <FacebookGroupDialog
        open={fbPrompt.visible}
        onClose={fbPrompt.dismiss}
        onJoin={fbPrompt.markJoined}
        seenCount={fbPrompt.seenCount}
      />
    </div>
  );
}